import { VerifiedUserOutlined } from '@mui/icons-material';
import type { Theme } from '@mui/material';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import type { DeliveryNote } from '../../../services/goods-receiving/goods-receiving.types';
import { TemperatureInputControls } from '../../self-check/temperature/record-page/components/temperature-input-controls';
import { WizardNavigation } from '../components/wizard-navigation';
import { WizardStepHeader } from '../components/wizard-step-header';
import { usePatchDraft } from '../hooks/use-draft-mutations';
import { getStepRoute, useWizardDraft } from '../hooks/use-wizard-draft';
import { WizardLayout } from './wizard-layout';

const DELIVERY_TEMPERATURE_RANGES = {
  refrigerated: { min: 0, max: 4, label: '0°C - 4°C' },
  frozen: { min: -25, max: -15, label: '-25°C - -15°C' },
} as const;

function validateDeliveryTemperature(
  temperature: number,
  perishableType: 'refrigerated' | 'frozen',
): { isCompliant: boolean; message: string } {
  const { min, max } = DELIVERY_TEMPERATURE_RANGES[perishableType];
  const isCompliant = temperature >= min && temperature <= max;
  return { isCompliant, message: `${temperature.toFixed(1)}°C (${min}°C - ${max}°C)` };
}

interface ChecklistFormState {
  hasPerishableGoods: boolean;
  perishableType: 'refrigerated' | 'frozen';
  temperature: number | null;
  vehicleHygiene: boolean;
  packagingIntact: boolean;
  notes: string;
}

function initStateFromDraft(draft: DeliveryNote | null | undefined): ChecklistFormState {
  const temp = draft?.temperature ?? null;
  return {
    hasPerishableGoods: temp != null,
    perishableType: temp != null && temp < 0 ? 'frozen' : 'refrigerated',
    temperature: temp,
    vehicleHygiene: draft?.vehicleHygiene ?? true,
    packagingIntact: draft?.packagingIntact ?? true,
    notes: draft?.notes ?? '',
  };
}

function useChecklistForm(draft: DeliveryNote | null | undefined) {
  const [state, setState] = useState<ChecklistFormState>(() => initStateFromDraft(draft));
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    if (draft && !synced) {
      setState(initStateFromDraft(draft));
      setSynced(true);
    }
  }, [draft, synced]);

  const setField = useCallback(
    <K extends keyof ChecklistFormState>(key: K, value: ChecklistFormState[K]) => {
      setState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handlePerishableChange = useCallback((value: string) => {
    const isPerishable = value === 'yes';
    setState((prev) => ({
      ...prev,
      hasPerishableGoods: isPerishable,
      temperature: isPerishable ? prev.temperature : null,
      perishableType: isPerishable ? prev.perishableType : 'refrigerated',
    }));
  }, []);

  return { state, setField, handlePerishableChange };
}

export function ChecklistStepPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draftId');

  const { data: draft } = useWizardDraft(draftId);
  const patchDraft = usePatchDraft();
  const { state, setField, handlePerishableChange } = useChecklistForm(draft);

  const temperatureValue = state.temperature ?? undefined;

  const temperatureValidation = useMemo(() => {
    if (temperatureValue === undefined) {
      return null;
    }
    return validateDeliveryTemperature(temperatureValue, state.perishableType);
  }, [temperatureValue, state.perishableType]);

  const handleTemperatureAdjust = useCallback(
    (delta: number) => {
      const current = temperatureValue ?? 0;
      setField('temperature', Math.round((current + delta) * 10) / 10);
    },
    [temperatureValue, setField],
  );

  const hasNonCompliance =
    !state.vehicleHygiene ||
    !state.packagingIntact ||
    (state.hasPerishableGoods &&
      temperatureValue !== undefined &&
      temperatureValidation?.isCompliant === false);

  const handleNext = () => {
    if (!draftId) {
      return;
    }
    patchDraft.mutate(
      {
        id: draftId,
        data: {
          temperature: state.hasPerishableGoods ? state.temperature : null,
          vehicleHygiene: state.vehicleHygiene,
          packagingIntact: state.packagingIntact,
          notes: state.notes || null,
          currentStep: 'storage',
        },
      },
      {
        onSuccess: () =>
          navigate(`${getStepRoute('storage')}?draftId=${draftId}`, { flushSync: true }),
      },
    );
  };

  return (
    <WizardLayout>
      <ChecklistForm
        state={state}
        temperatureValue={temperatureValue}
        temperatureValidation={temperatureValidation}
        hasNonCompliance={hasNonCompliance}
        theme={theme}
        onPerishableChange={handlePerishableChange}
        onPerishableTypeChange={(type) => {
          setField('perishableType', type);
          setField('temperature', null);
        }}
        onVehicleHygieneChange={(v) => setField('vehicleHygiene', v)}
        onPackagingIntactChange={(v) => setField('packagingIntact', v)}
        onTemperatureChange={(temp) => setField('temperature', temp ?? null)}
        onTemperatureAdjust={handleTemperatureAdjust}
        onNotesChange={(v) => setField('notes', v)}
      />
      <WizardNavigation
        canGoBack={true}
        canGoNext={true}
        onBack={() => navigate(`${getStepRoute('items')}?draftId=${draftId}`, { flushSync: true })}
        onNext={handleNext}
        isLoading={patchDraft.isPending}
      />
    </WizardLayout>
  );
}

interface ChecklistFormProps {
  state: ChecklistFormState;
  temperatureValue: number | undefined;
  temperatureValidation: { isCompliant: boolean; message: string } | null;
  hasNonCompliance: boolean;
  theme: Theme;
  onPerishableChange: (value: string) => void;
  onPerishableTypeChange: (type: 'refrigerated' | 'frozen') => void;
  onVehicleHygieneChange: (value: boolean) => void;
  onPackagingIntactChange: (value: boolean) => void;
  onTemperatureChange: (temp: number | null | undefined) => void;
  onTemperatureAdjust: (delta: number) => void;
  onNotesChange: (value: string) => void;
}

function ChecklistForm({
  state,
  temperatureValue,
  temperatureValidation,
  hasNonCompliance,
  theme,
  onPerishableChange,
  onPerishableTypeChange,
  onVehicleHygieneChange,
  onPackagingIntactChange,
  onTemperatureChange,
  onTemperatureAdjust,
  onNotesChange,
}: ChecklistFormProps) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <WizardStepHeader
        step="checklist"
        icon={<VerifiedUserOutlined />}
        title={t('goodsReceiving.checklistTitle')}
        subtitle={t('goodsReceiving.checklistSubtitle')}
      />
      <FormControl component="fieldset">
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {t('goodsReceiving.hasPerishableGoods')}
        </Typography>
        <RadioGroup
          row
          value={state.hasPerishableGoods ? 'yes' : 'no'}
          onChange={(e) => onPerishableChange(e.target.value)}
        >
          <FormControlLabel value="yes" control={<Radio />} label={t('goodsReceiving.yes')} />
          <FormControlLabel value="no" control={<Radio />} label={t('goodsReceiving.no')} />
        </RadioGroup>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          {t('goodsReceiving.perishableHint')}
        </Typography>
      </FormControl>
      {state.hasPerishableGoods && (
        <FormControl component="fieldset">
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {t('goodsReceiving.perishableType')}
          </Typography>
          <RadioGroup
            row
            value={state.perishableType}
            onChange={(e) => onPerishableTypeChange(e.target.value as 'refrigerated' | 'frozen')}
          >
            <FormControlLabel
              value="refrigerated"
              control={<Radio />}
              label={t('goodsReceiving.refrigerated')}
            />
            <FormControlLabel
              value="frozen"
              control={<Radio />}
              label={t('goodsReceiving.frozen')}
            />
          </RadioGroup>
        </FormControl>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={state.vehicleHygiene}
              onChange={(e) => onVehicleHygieneChange(e.target.checked)}
            />
          }
          label={t('goodsReceiving.vehicleHygiene')}
        />
        <FormControlLabel
          control={
            <Switch
              checked={state.packagingIntact}
              onChange={(e) => onPackagingIntactChange(e.target.checked)}
            />
          }
          label={t('goodsReceiving.packagingIntact')}
        />
      </Box>
      {state.hasPerishableGoods && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2">{t('goodsReceiving.temperatureGoods')}</Typography>
          <TemperatureInputControls
            temperature={temperatureValue}
            validation={temperatureValidation}
            hasValue={temperatureValue !== undefined}
            isCompliant={temperatureValidation?.isCompliant ?? null}
            onTemperatureChange={onTemperatureChange}
            onTemperatureAdjust={onTemperatureAdjust}
            theme={theme}
          />
        </Box>
      )}
      {hasNonCompliance && (
        <Alert severity="warning">{t('goodsReceiving.nonComplianceWarning')}</Alert>
      )}
      <TextField
        label={t('goodsReceiving.additionalNotes')}
        value={state.notes}
        onChange={(e) => onNotesChange(e.target.value)}
        fullWidth
        multiline
        rows={4}
        helperText={t('goodsReceiving.optional')}
      />
    </Box>
  );
}
